import {
    MessageType,
    type ClientMessage,
    type ServerMessage,
    type ProviderCredentials,
    ConnectionState
} from './types';

/**
 * WebSocket Client for Ocktra Server
 * Handles authentication, provider configuration, and real-time data streaming
 */
export class WebSocketClient {
    private static instance: WebSocketClient | null = null;
    private static toastHandler: ((message: string, type: 'success' | 'error' | 'warning' | 'info') => void) | null = null;
    private ws: WebSocket | null = null;
    private url: string;
    private connectionState: ConnectionState = ConnectionState.DISCONNECTED;

    // Event handlers
    private messageHandlers: Map<string, Set<(data: any) => void>> = new Map();
    private stateChangeHandlers: Set<(state: ConnectionState) => void> = new Set();

    private constructor() {
        const metaEnv = (import.meta as any).env as { VITE_WS_URL?: string } | undefined;
        this.url = metaEnv?.VITE_WS_URL || 'ws://localhost:8081';
    }

    /**
     * Get singleton instance
     */
    public static getInstance(): WebSocketClient {
        if (!WebSocketClient.instance) {
            WebSocketClient.instance = new WebSocketClient();
        }
        return WebSocketClient.instance;
    }

    /**
     * Set global toast handler
     */
    public static setToastHandler(handler: (message: string, type: 'success' | 'error' | 'warning' | 'info') => void): void {
        WebSocketClient.toastHandler = handler;
    }

    /**
     * Clear global toast handler
     */
    public static clearToastHandler(): void {
        WebSocketClient.toastHandler = null;
    }

    /**
     * Connect to WebSocket server
     */
    public connect(token: string): Promise<void> {
        return new Promise((resolve, reject) => {
            if (this.connectionState === ConnectionState.CONNECTING) {
                reject(new Error('Connection already in progress'));
                return;
            }

            if (this.ws && this.connectionState === ConnectionState.CONNECTED) {
                resolve();
                return;
            }

            this.updateState(ConnectionState.CONNECTING);

            try {
                this.ws = new WebSocket(this.url + `?token=${encodeURIComponent(token)}`);

                this.ws.onopen = () => {
                    console.log('[WebSocket] Connected');
                    this.updateState(ConnectionState.CONNECTED);
                    this.showToast('Connected to server', 'success');
                    resolve();
                };

                this.ws.onmessage = (event) => {
                    this.handleMessage(event.data);
                };

                this.ws.onerror = (error) => {
                    console.error('[WebSocket] Error:', error);
                    this.updateState(ConnectionState.ERROR);
                    this.showToast('Connection failed', 'error');
                    reject(new Error('WebSocket connection failed'));
                };

                this.ws.onclose = () => {
                    console.log('[WebSocket] Connection closed');
                    this.updateState(ConnectionState.DISCONNECTED);
                };
            } catch (error) {
                console.error('[WebSocket] Error:', error);
                this.updateState(ConnectionState.ERROR);
                reject(error);
            }
        });
    }

    /**
     * Configure data feed provider
     */
    public configureProvider(providerType: string, credentials: ProviderCredentials): void {
        this.send({
            type: MessageType.CONFIGURE_PROVIDER,
            providerType,
            credentials
        });
    }

    /**
     * Subscribe to symbols
     */
    public subscribe(symbols: string[]): void {
        this.send({
            type: MessageType.SUBSCRIBE,
            payload: { symbols }
        });
    }

    /**
     * Unsubscribe from symbols
     */
    public unsubscribe(symbols: string[]): void {
        this.send({
            type: MessageType.UNSUBSCRIBE,
            payload: { symbols }
        });
    }

    /**
     * Register handler for data from specific channel
     */
    public onChannelData(channel: string, handler: (data: any) => void): () => void {
        if (!this.messageHandlers.has(channel)) {
            this.messageHandlers.set(channel, new Set());
        }
        this.messageHandlers.get(channel)!.add(handler);

        // Return cleanup function
        return () => {
            const handlers = this.messageHandlers.get(channel);
            if (handlers) {
                handlers.delete(handler);
                if (handlers.size === 0) {
                    this.messageHandlers.delete(channel);
                }
            }
        };
    }

    public sendMessage(message: ClientMessage): void {
        this.send(message);
    }

    /**
     * Register handler for connection state changes
     */
    public onStateChange(handler: (state: ConnectionState) => void): () => void {
        this.stateChangeHandlers.add(handler);
        // Return cleanup function
        return () => {
            this.stateChangeHandlers.delete(handler);
        };
    }



    /**
     * Get current connection state
     */
    public getState(): ConnectionState {
        return this.connectionState;
    }

    /**
     * Check if connected
     */
    public isConnected(): boolean {
        return this.connectionState === ConnectionState.CONNECTED;
    }

    /**
     * Disconnect from WebSocket server
     */
    public disconnect(): void {
        console.log('[WebSocket] Disconnecting');

        if (this.ws) {
            this.ws.close();
            this.ws = null;
        }

        this.updateState(ConnectionState.DISCONNECTED);
    }

    /**
     * Send message to server
     */
    private send(message: ClientMessage): void {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
            console.warn('[WebSocket] Not connected, message not sent');
            return;
        }

        this.ws.send(JSON.stringify(message));
    }

    private updateState(state: ConnectionState): void {
        this.connectionState = state;
        this.stateChangeHandlers.forEach(handler => handler(state));
    }

    private showToast(message: string, type: 'success' | 'error' | 'warning' | 'info'): void {
        if (WebSocketClient.toastHandler) {
            WebSocketClient.toastHandler(message, type);
        }
    }

    /**
     * Handle incoming messages
     */
    private handleMessage(data: string): void {
        try {
            const message: ServerMessage = JSON.parse(data);

            switch (message.type) {
                case MessageType.SUCCESS:
                    console.log('[WebSocket] Success:', message);
                    break;

                case MessageType.ERROR: {
                    const errorMsg = message as { error?: string };
                    console.error('[WebSocket] Error:', message);
                    this.showToast(errorMsg.error || 'An error occurred', 'error');
                    break;
                }

                case MessageType.TICKS: {
                    const tickMsg = message as { type: 'ticks'; payload: { ticks: any[] } };
                    const handlers = this.messageHandlers.get('ticks');
                    if (handlers) {
                        handlers.forEach(handler => handler(tickMsg.payload.ticks));
                    }
                    break;
                }

                case MessageType.PROVIDER_STREAM_CONNECTED:
                    console.log('[WebSocket] Provider stream connected');
                    this.showToast('Provider stream connected', 'success');
                    break;

                case MessageType.PROVIDER_STREAM_DISCONNECTED:
                    console.log('[WebSocket] Provider stream disconnected');
                    this.showToast('Provider stream disconnected', 'warning');
                    break;

                default:
                    console.log('[WebSocket] Message:', message);
            }
        } catch (error) {
            console.error('[WebSocket] Parse error:', error);
        }
    }
}

// Export singleton instance
export const wsClient = WebSocketClient.getInstance();
